import * as React from "react";
import { cn } from "@/lib/utils";

export type TextareaProps =
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    error?: boolean;
    /**
     * Optional id of the hint/error element describing this textarea — rendered
     * as `aria-describedby` (WCAG 2.2 3.3.1 Error Identification, research
     * 2026-09-18). Error pattern:
     *   <Textarea error describedBy="bio-error" ... />
     *   {error && <p id="bio-error" role="alert">Máximo 500 caracteres.</p>}
     * (Direct `aria-describedby` via props still wins — spread stays last.)
     */
    describedBy?: string;
    /**
     * "hero": variante marketing opt-in — rounded 20px + foco hero-focus, superficie
     * opaca (2026-09-20 FASE 3, research R2 2026: sin blur sobre campo opaco).
     * El default queda intacto y sin anillo (dueño 2026-09-08). El anillo hero
     * pisa el `box-shadow:none` global de textareas por mayor especificidad de clase.
     * 2026-09-21 FASE 3 FIXER (anillo 3:1: hero-focus-ring sólido en variant hero).
     */
    variant?: "default" | "hero";
  };

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, error, describedBy, variant = "default", ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        className={cn(
          // SSOT --radius-field (16px, F6) — multi-line nunca pill para no recortar texto
          "w-full rounded-[var(--radius-field,16px)] border border-[var(--border)] bg-[var(--background-elevated)] px-3.5 py-3 text-foreground placeholder:text-muted-foreground",
          "font-sans max-sm:text-base sm:text-[var(--step--1)] leading-[1.6] tracking-normal",
          // 2026-10-03 RUN12 FASE 8 N1 [textarea-sm15-to-step--1] (research 2026 Smashing Magazine fluid type + Utopia: step--1 13.34/14.55/15.04 vs 15px fijo Δ0.34/0.45/0.04 sub-perceptual; max-sm:text-base intacto anti-zoom iOS).
          "resize-y shadow-[inset_0_1px_0_rgba(255,255,255,0.025)] transition-[border-color,box-shadow,background-color] duration-[var(--duration-fast)] ease-[var(--ease-out-quart)]",
          // 2026-09-23 FASE 3 FIX diseño H6 (research 2026 WCAG 2.2 2.4.7/2.4.11:
          // anillo default simétrico con input.tsx:39 — 3px accent 40% + borde
          // accent 45%; antes el textarea solo cambiaba el borde = indicador
          // asimétrico entre campos del mismo formulario).
          "focus-visible:outline-none focus-visible:border-[color-mix(in_srgb,var(--accent)_45%,var(--border))] focus-visible:shadow-[0_0_0_3px_color-mix(in_srgb,var(--accent)_65%,transparent)]",
          variant === "hero" &&
            "rounded-[var(--radius-hero-bento,20px)] bg-[var(--background-elevated)] focus-visible:border-[var(--hero-focus-ring,#a855f7)] focus-visible:shadow-[0_0_0_3px_var(--hero-focus-ring,#a855f7)]",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          error &&
            "border-[var(--danger)] focus-visible:ring-2 focus-visible:ring-[var(--danger)]",
          className,
        )}
        aria-invalid={error || undefined}
        aria-describedby={describedBy}
        {...props}
      />
    );
  },
);
Textarea.displayName = "Textarea";
